import { useEffect, useRef, useState, useCallback } from "react";
import { MapContainer, TileLayer, Marker, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import type { GpsData } from "../types/telemetry";
import { loadTracks } from "../tracks/tracks";

// Fix default Leaflet marker icon issue with bundlers
const markerIcon = new L.Icon({
  iconUrl: "/images/marker-icon.png",
  iconRetinaUrl: "/images/marker-icon-2x.png",
  shadowUrl: "/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

interface MapViewProps {
  gps: GpsData;
  trail: [number, number][];
  fallbackTrackId?: string | null;
}

/** Auto-pans the map to follow the current position */
function MapFollower({ lat, lng }: { lat: number; lng: number }) {
  const map = useMap();
  const isFirstRef = useRef(true);

  useEffect(() => {
    if (isFirstRef.current) {
      map.setView([lat, lng], 15);
      isFirstRef.current = false;
    } else {
      map.panTo([lat, lng], { animate: false });
    }
  }, [map, lat, lng]);

  return null;
}

function MapViewport({ center, zoom }: { center: [number, number]; zoom: number }) {
  const map = useMap();

  useEffect(() => {
    map.setView(center, zoom);
  }, [map, center, zoom]);

  return null;
}

function MapResizer() {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();
    const observer = new ResizeObserver(() => {
      map.invalidateSize();
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [map]);

  return null;
}

export function MapView({ gps, trail, fallbackTrackId = null }: MapViewProps) {
  const hasPosition = gps.latitude !== null && gps.longitude !== null;
  const [fallbackTrackView, setFallbackTrackView] = useState<{ center: [number, number]; zoom: number } | null>(null);
  const [useMph, setUseMph] = useState(() => {
    try { return localStorage.getItem("vtms-speed-unit") === "mph"; }
    catch { return false; }
  });

  useEffect(() => {
    let cancelled = false;

    if (!fallbackTrackId) {
      setFallbackTrackView(null);
      return;
    }

    void (async () => {
      const tracks = await loadTracks();
      if (cancelled) return;
      const track = tracks.find((candidate) => candidate.id === fallbackTrackId);
      if (!track) {
        console.warn(`[map] Unknown fallback track id: ${fallbackTrackId}`);
        setFallbackTrackView(null);
        return;
      }
      setFallbackTrackView({
        center: [track.center.lat, track.center.lng],
        zoom: track.zoom,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [fallbackTrackId]);

  const toggleSpeedUnit = useCallback(() => {
    setUseMph((prev) => {
      const next = !prev;
      try { localStorage.setItem("vtms-speed-unit", next ? "mph" : "kmh"); }
      catch { /* ignore */ }
      return next;
    });
  }, []);

  // Default center: roughly center of US if no GPS data yet
  const center: [number, number] = hasPosition
    ? [gps.latitude!, gps.longitude!]
    : fallbackTrackView?.center ?? [39.8283, -98.5795];

  const zoom = hasPosition ? 15 : fallbackTrackView?.zoom ?? 4;

  return (
    <div className="map-container">
      <MapContainer
        center={center}
        zoom={zoom}
        className="map"
        zoomControl={true}
        attributionControl={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapResizer />

        {!hasPosition && <MapViewport center={center} zoom={zoom} />}

        {hasPosition && (
          <>
            <MapFollower lat={gps.latitude!} lng={gps.longitude!} />
            <Marker
              position={[gps.latitude!, gps.longitude!]}
              icon={markerIcon}
            />
          </>
        )}

        {trail.length > 1 && (
          <Polyline
            positions={trail}
            pathOptions={{
              color: "#f59e0b",
              weight: 3,
              opacity: 0.7,
            }}
          />
        )}
      </MapContainer>

      {/* GPS info overlay */}
      {hasPosition && (
        <div className="map-overlay">
          <span>
            {gps.latitude!.toFixed(6)}, {gps.longitude!.toFixed(6)}
          </span>
          {gps.speed !== null && (
            <button
              className="map-overlay__speed-toggle"
              onClick={toggleSpeedUnit}
              title="Click to toggle mph / km/h"
            >
              {useMph
                ? `${(gps.speed * 2.23694).toFixed(1)} mph`
                : `${(gps.speed * 3.6).toFixed(1)} km/h`}
            </button>
          )}
          {gps.altitude !== null && <span>{gps.altitude.toFixed(0)}m</span>}
        </div>
      )}
    </div>
  );
}
